'use client'
import { useState } from 'react'
import { motion } from 'framer-motion'
import { useLanguage } from '@/lib/context/LanguageContext'
import { faqItems } from '@/lib/data/faq'
import type { Lang } from '@/lib/i18n'
import SectionHeader from '@/components/ui/SectionHeader'
import { FadeInUp, Stagger, StaggerItem } from '@/components/ui/FadeInUp'
import { toPersianNums } from '@/lib/persian'
import { EASE } from '@/lib/constants/animations'

const GRADIENT = 'linear-gradient(135deg,#0335b7,#00b4d8)'

function Item({
  index,
  num,
  question,
  answer,
  isOpen,
  onToggle,
}: {
  index: number
  num: string
  question: string
  answer: string
  isOpen: boolean
  onToggle: () => void
}) {
  const panelId = `faq-panel-${index}`

  return (
    <motion.div
      className="rounded-2xl border bg-white overflow-hidden"
      animate={{
        borderColor: isOpen ? 'rgba(3,53,183,0.25)' : 'rgba(11,22,41,0.07)',
        boxShadow: isOpen ? '0 16px 44px rgba(3,53,183,0.12)' : '0 1px 2px rgba(11,22,41,0.04)',
      }}
      whileHover={isOpen ? undefined : { y: -3 }}
      transition={{ duration: 0.25, ease: EASE }}
    >
      <button
        className="w-full flex items-center gap-4 p-5 md:p-6 text-start group focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-controls={panelId}
      >
        {/* Number chip */}
        <span
          className="flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center font-display font-bold text-[13px] transition-colors duration-300"
          style={
            isOpen
              ? { background: GRADIENT, color: '#fff' }
              : { background: 'rgba(11,22,41,0.04)', color: '#475569' }
          }
        >
          {num}
        </span>

        {/* Question */}
        <span
          className="flex-1 min-w-0 font-semibold text-sm md:text-base leading-relaxed transition-colors duration-200"
          style={{ color: isOpen ? '#0335b7' : '#0b1629' }}
        >
          {question}
        </span>

        {/* Toggle icon */}
        <span
          className="flex-shrink-0 w-8 h-8 rounded-full border flex items-center justify-center transition-all duration-300"
          style={{
            background: isOpen ? GRADIENT : 'transparent',
            borderColor: isOpen ? 'transparent' : 'rgba(11,22,41,0.14)',
            transform: isOpen ? 'rotate(45deg)' : 'none',
          }}
        >
          <svg
            className="w-3.5 h-3.5"
            fill="none"
            stroke="currentColor"
            strokeWidth={2.5}
            viewBox="0 0 24 24"
            style={{ color: isOpen ? '#ffffff' : '#0b1629' }}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
          </svg>
        </span>
      </button>

      <motion.div
        id={panelId}
        role="region"
        aria-hidden={!isOpen}
        initial={false}
        animate={{ height: isOpen ? 'auto' : 0, opacity: isOpen ? 1 : 0 }}
        transition={{ duration: 0.32, ease: EASE }}
        className="overflow-hidden"
      >
        <div className="px-5 md:px-6 pb-6">
          <div className="h-px bg-ink/8 mb-4 ms-[3.25rem]" />
          <p className="text-sm leading-loose ms-[3.25rem]" style={{ color: '#475569' }}>
            {answer}
          </p>
        </div>
      </motion.div>
    </motion.div>
  )
}

export default function FAQ() {
  const { t, lang } = useLanguage()
  const l = lang as Lang
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  return (
    <section id="faq" className="py-16 md:py-24 lg:py-32 border-b border-ink/5 bg-[#f6f8fc]">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <SectionHeader num="06" label={t.faq.section} />

        <div className="flex flex-col md:flex-row gap-8 md:gap-16 items-start">
          {/* ── Sticky intro panel ── */}
          <div className="w-full md:w-2/5 md:flex-shrink-0 md:sticky md:top-28 md:self-start">
            <FadeInUp>
              <h2 className="font-display font-black text-3xl sm:text-4xl md:text-5xl text-ink leading-tight mb-5">
                {t.faq.heading}
              </h2>
              <p className="text-muted text-sm md:text-base leading-relaxed mb-8 max-w-sm">
                {t.faq.sub}
              </p>

              {/* Count + contact card */}
              <div className="rounded-2xl border border-ink/8 bg-white p-5 shadow-sm max-w-sm">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold text-ink leading-snug">
                    {l === 'fa'
                      ? 'گفت‌وگوی بعدی را ازاینجا آغاز کنید'
                      : 'Start the next conversation here'}
                  </span>
                </div>

                <div className="h-px bg-ink/8 my-4" />

                <p className="text-sm text-muted">
                  {l === 'fa' ? 'هنوز سوالی دارید؟ ' : 'Still have a question? '}
                  <a
                    href="#contact"
                    className="font-semibold text-primary hover:opacity-70 transition-opacity"
                  >
                    {l === 'fa' ? 'با ما صحبت کنید' : 'Talk to us'}
                  </a>
                </p>
              </div>
            </FadeInUp>
          </div>

          {/* ── Accordion ── */}
          <div className="w-full md:flex-1 md:min-w-0">
            <Stagger className="flex flex-col gap-3" staggerDelay={0.06}>
              {faqItems.map((item, i) => (
                <StaggerItem key={item.question.en}>
                  <Item
                    index={i}
                    num={
                      l === 'fa'
                        ? toPersianNums(String(i + 1).padStart(2, '0'))
                        : String(i + 1).padStart(2, '0')
                    }
                    question={item.question[l]}
                    answer={item.answer[l]}
                    isOpen={openIndex === i}
                    onToggle={() => setOpenIndex(openIndex === i ? null : i)}
                  />
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </div>
      </div>
    </section>
  )
}
